import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
// MUI Icons
import MessagesIcon from "@mui/icons-material/Message";
import { BASE_URL } from "../../const/URL";

// ----------------------------------------------------------------------

export type MessagesPopoverProps = IconButtonProps & {
  customerId?: string;
};

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: 5,
    top: 5,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
  },
}));

export function MessagesPopover({ sx, ...other }: MessagesPopoverProps) {
  const navigate = useNavigate();
  const [Messages, setMessages] = useState();
  const [MessagesAll, setMessagesAll] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(
    null
  );

  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/messages/unread-count`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "enterprise_portal_access_Token"
          )}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch Messages");

      const data = await response.json();

      if (data.isSuccess) {
        setMessagesAll(data.dataBundle);
        setMessages(data.dataBundle);
      }
    } catch (err) {
      setError("Failed to load Messages. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch messages immediately when the component mounts (auth / loading)
    fetchMessages();

    // Timer polling turned off — call only on mount / auth load
    // // Set up an interval to fetch messages every 30 seconds
    // const intervalId = setInterval(fetchMessages, 60000);
    //
    // // Clear the interval when the component unmounts
    // return () => clearInterval(intervalId);
  }, [fetchMessages]);

  const totalUnRead = MessagesAll;

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  return (
    <>
      <IconButton
        color={openPopover ? "primary" : "default"}
        onClick={handleOpenPopover}
        sx={sx}
        {...other}
      >
        <StyledBadge badgeContent={totalUnRead} color="error" max={9}>
          <MessagesIcon fontSize="medium" />
        </StyledBadge>
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: 24,
              border: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          sx={{ py: 2, pl: 2.5, pr: 1.5, bgcolor: "background.neutral" }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">Messages</Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              {totalUnRead} unread messages
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Divider sx={{ borderStyle: "dashed" }} />

        <Box sx={{ p: 1, bgcolor: "background.neutral" }}>
          <Button
            fullWidth
            color="inherit"
            onClick={() => {
              navigate("/messages/inbox");
              handleClosePopover();
            }}
            sx={{
              fontWeight: "medium",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            View All Messages
          </Button>
        </Box>
      </Popover>
    </>
  );
}
